import rootRender from '@react/utils/root-render';
import Button from '@react/components/button';
import {
  FeaturedPageEntity,
  NavigationData,
  NavigationSubMenu,
} from './nav.types';
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { clsx } from 'clsx';
import { Transition, SwitchTransition } from 'react-transition-group';
import { gsap } from 'gsap';
import { getDataFromPage, pressedEnter } from '@react/utils';
import { useIsBreakpoint, useIsScrolled } from '@react/utils/hooks';
import { ANIMATION_CLASSES } from '@/js/lib/constants';
import { DLNavClick } from '@/js/general/data-layer';

const data: NavigationData = getDataFromPage('react-nav-data');

function handleDataLayer(category: string, level: number, text: string) {
  DLNavClick({
    nav_category: category,
    nav_level: level,
    click_text: text,
  });
}

function Nav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [secondaryOpen, setSecondaryOpen] = useState(false);
  const [tertiaryOpen, setTertiaryOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);

  const [secondaryMenuId, setSecondaryMenuId] = useState<string | undefined>(
    undefined
  );
  const [secondaryData, setSecondaryData] =
    useState<NavigationSubMenu>(undefined);
  const [tertiaryMenuId, setTertiaryMenuId] = useState<string>(undefined);
  const [tertiaryData, setTertiaryData] =
    useState<NavigationSubMenu>(undefined);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const secondaryContentTopRef = useRef<HTMLDivElement>(null);
  const tertiaryContentTopRef = useRef<HTMLDivElement>(null);

  const [wrapperIsScrolled, setWrapperIsScrolled] = useIsScrolled(wrapperRef);

  const setNotScrolled = useCallback(
    () => setWrapperIsScrolled(false),
    [setWrapperIsScrolled]
  );

  const {
    homePageCta,
    logoImageUrl,
    bottomCta,
    secondaryLinks,
    primaryLinks,
    searchLink,
  } = data;

  const openMobile = () => setMobileOpen(true);

  const closeTertiary = useCallback(() => {
    setTertiaryOpen(false);
    setTertiaryMenuId(undefined);
  }, []);

  const closeSecondary = useCallback(() => {
    setSecondaryOpen(false);
    setSecondaryMenuId(undefined);
    closeTertiary();
  }, [closeTertiary]);

  const closeMobile = useCallback(() => {
    closeSecondary();
    closeTertiary();
    setMobileOpen(false);
  }, [closeSecondary, closeTertiary]);

  const handleSecondaryMenu = useCallback(
    (id: string) => {
      if (id === secondaryMenuId) {
        return closeSecondary();
      }
      setSecondaryMenuId(id);
      setSecondaryOpen(true);
    },
    [secondaryMenuId, closeSecondary]
  );

  const handleTertiaryMenu = useCallback(
    (id: string) => {
      if (id === tertiaryMenuId) {
        return closeTertiary();
      }
      setTertiaryMenuId(id);
      setTertiaryOpen(true);
    },
    [tertiaryMenuId, closeTertiary]
  );

  useEffect(() => {
    document.body.style.overflow = secondaryOpen ? 'hidden' : null;
    if (secondaryOpen) setLanguageOpen(false);
  }, [secondaryOpen]);

  useEffect(() => {
    const secondaryMenu = primaryLinks.find(({ id }) => id === secondaryMenuId);
    setSecondaryData(secondaryMenu);

    if (!secondaryMenu) return;

    const tertiaryMenu = secondaryMenu.subMenus.find(
      ({ id }) => id === tertiaryMenuId
    );
    setTertiaryData(tertiaryMenu);
  }, [secondaryMenuId, tertiaryMenuId, primaryLinks]);

  useEffect(() => {
    if (!secondaryContentTopRef.current || !tertiaryContentTopRef.current)
      return;

    const newHeight = Math.max(
      secondaryContentTopRef.current.scrollHeight,
      tertiaryContentTopRef.current.scrollHeight
    );

    // Dynamically set the CSS variable without using inline styles
    document.documentElement.style.setProperty(
      '--menu-top-height',
      `${newHeight}px`
    );
  }, [secondaryData, tertiaryData]);

  useEffect(() => {
    if (!tertiaryData) closeTertiary();
  }, [tertiaryData, closeTertiary]);

  const renderLink = useCallback(
    (link: NavigationSubMenu) => (
      <li className="nav__link" key={link.id}>
        <a
          href={link.url}
          onClick={() => handleDataLayer(link.title, 1, link.title)}
        >
          <span>{link.title}</span>
        </a>
      </li>
    ),
    []
  );

  const renderLinks = useCallback(
    (item: NavigationSubMenu) =>
      item.subMenus.length ? (
        <li key={item.id}>
          <button
            className={clsx('nav__sub-menu-link', {
              'nav__sub-menu-link--open':
                secondaryOpen && secondaryMenuId === item.id,
            })}
            aria-expanded={secondaryOpen && secondaryMenuId === item.id}
            aria-controls="nav-secondary"
            onClick={() => handleSecondaryMenu(item.id)}
          >
            <span>{item.title}</span>
          </button>
        </li>
      ) : (
        renderLink(item)
      ),
    [secondaryOpen, secondaryMenuId, handleSecondaryMenu, renderLink]
  );

  return (
    <>
      <button
        className={clsx('nav__backdrop', {
          'nav__backdrop--active': secondaryOpen,
        })}
        onClick={closeSecondary}
        title="Close menu"
      />
      <nav
        className={clsx('nav', {
          'nav--open': mobileOpen,
        })}
      >
        <div
          className={clsx('nav__wrapper', {
            scrolled: wrapperIsScrolled,
          })}
          ref={wrapperRef}
        >
          <div className="nav__inner">
            <a href={homePageCta.url} title={homePageCta.text}>
              <picture className="nav__logo">
                <source srcSet={logoImageUrl} media="(min-width: 52.5em)" />
                <img
                  src={logoImageUrl} // May need mobile logo here
                  width={144}
                  height={144}
                  alt=""
                />
              </picture>
            </a>
            <div
              className={clsx('nav__links', {
                'nav__links--menu-open': secondaryOpen,
              })}
            >
              <div className="nav__links-top">
                <ul className="nav__links-list">
                  {!!primaryLinks.length && primaryLinks.map(renderLinks)}
                </ul>
              </div>
              <div className="nav__links-bottom">
                <ul className="nav__links-list">
                  {secondaryLinks.map(item => (
                    <li className="nav__link" key={item.text}>
                      <a
                        href={item.url}
                        onClick={() => handleDataLayer(item.text, 1, item.text)}
                      >
                        <span>{item.text}</span>
                      </a>
                    </li>
                  ))}
                  {searchLink.url !== '' && (
                    <li className="nav__link nav__link--hide-lg">
                      <a
                        href={searchLink.url}
                        onClick={() =>
                          handleDataLayer(searchLink.text, 1, searchLink.text)
                        }
                      >
                        <span>{searchLink.text}</span>
                        <i className="icon-search" />
                      </a>
                    </li>
                  )}
                  <li className="nav-mobile__language">
                    <LanguageSelector
                      onOpen={setNotScrolled}
                      isOpen={languageOpen}
                      setIsOpen={setLanguageOpen}
                    />
                  </li>
                </ul>
              </div>
            </div>
          </div>
          {bottomCta?.text && (
            <a
              className="nav__button-bottom"
              href={bottomCta.url}
              onClick={() => handleDataLayer(bottomCta.text, 1, bottomCta.text)}
            >
              <Button.Inner content={bottomCta.text} />
              <i className="icon-arrow-right" />
            </a>
          )}
        </div>
        <div className="nav__language">
          <LanguageSelector isOpen={languageOpen} setIsOpen={setLanguageOpen} />
        </div>
        <SecondaryMenu
          isOpen={secondaryOpen}
          close={closeSecondary}
          menu={secondaryData}
          handleTertiaryMenu={handleTertiaryMenu}
          tertiaryMenuId={tertiaryMenuId}
          secondaryContentTopRef={secondaryContentTopRef}
          tertiaryOpen={tertiaryOpen}
          tertiaryMenu={
            <TertiaryMenu
              isOpen={tertiaryOpen}
              menu={tertiaryData}
              rootMenuTitle={secondaryData?.title}
              tertiaryContentTopRef={tertiaryContentTopRef}
              close={closeTertiary}
            />
          }
        />
      </nav>

      <div
        className={clsx('nav-mobile', {
          'nav-mobile--open': mobileOpen,
        })}
      >
        <a href={bottomCta.url} className="nav-mobile__button-left">
          <span>{bottomCta.text}</span>
          <i className="icon-arrow-right" />
        </a>
        <div className="nav-mobile__buttons">
          <a href={searchLink.url} title={searchLink.text}>
            <i className="nav-mobile__icon icon-search" />
          </a>
          {mobileOpen ? (
            <button
              type="button"
              onClick={closeMobile}
              onKeyUp={e => pressedEnter(e, closeMobile)}
              title="Close menu"
            >
              <i className="nav-mobile__icon icon-cross" />
            </button>
          ) : (
            <button
              type="button"
              onClick={openMobile}
              onKeyUp={e => pressedEnter(e, openMobile)}
              title="Open menu"
            >
              <i className="nav-mobile__icon icon-menu" />
            </button>
          )}
        </div>
      </div>
    </>
  );
}

const SecondaryMenu = ({
  isOpen,
  menu,
  close,
  tertiaryMenu,
  tertiaryMenuId,
  tertiaryOpen,
  handleTertiaryMenu,
  secondaryContentTopRef,
}: {
  isOpen: boolean;
  menu?: NavigationSubMenu;
  close: () => void;
  tertiaryMenu: React.ReactNode;
  tertiaryMenuId?: string;
  tertiaryOpen: boolean;
  handleTertiaryMenu: (id: string) => void;
  secondaryContentTopRef: React.MutableRefObject<HTMLDivElement>;
}) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const linksRef = useRef<HTMLDivElement>(null);
  const [isScrolled] = useIsScrolled(innerRef);
  const { resourceStrings, featuredPages } = data;
  const isMobile = !useIsBreakpoint('lg');

  useLayoutEffect(() => {
    if (!isOpen) return;
    closeRef.current.focus();
  }, [isOpen, menu]);

  const hasFeaturedPages = featuredPages && featuredPages.length > 0;

  return (
    <section
      id="nav-secondary"
      className={clsx('nav__secondary-menu', {
        'nav__secondary-menu--open': isOpen,
      })}
    >
      <div
        className={clsx('nav__close-button-wrapper', {
          'nav__close-button-wrapper--tertiary-open': tertiaryOpen,
          'nav__close-button-wrapper--featured': isOpen && hasFeaturedPages,
        })}
      >
        <button
          type="button"
          title="Close menu"
          className="nav__close-button"
          onClick={close}
          ref={closeRef}
        >
          <Button.Inner content={<i className="icon-cross" />} />
        </button>
      </div>
      <div
        className={clsx('nav__secondary-menu-inner', {
          scrolled: isScrolled,
        })}
        ref={innerRef}
      >
        {menu ? (
          <>
            <NavTransition
              contentRef={secondaryContentTopRef}
              contentKey={menu.id}
            >
              <>
                <button
                  type="button"
                  className="nav__secondary-menu-back"
                  onClick={close}
                >
                  <i className="icon-arrow-left" />
                  <span>{resourceStrings.back}</span>
                </button>
                <div
                  className="nav__secondary-menu-top-content"
                  ref={secondaryContentTopRef}
                >
                  <p className="nav__secondary-menu-copy">{menu.panelCopy}</p>
                  {menu.panelLinkText ? (
                    <a
                      className="nav__secondary-menu-page-link"
                      href={menu.url}
                      onClick={() =>
                        handleDataLayer(menu.title, 2, menu.panelLinkText)
                      }
                    >
                      <span>{menu.panelLinkText}</span>
                      <i className="icon-arrow-right" />
                    </a>
                  ) : null}
                </div>
              </>
            </NavTransition>

            <NavTransition
              contentRef={linksRef}
              contentKey={menu.id}
              delay={0.2}
            >
              <div className="nav__secondary-links" ref={linksRef}>
                <ul
                  className={clsx('nav__secondary-links-list', {
                    'nav__secondary-links-list--open': tertiaryOpen,
                  })}
                >
                  {menu.subMenus.map(subMenu => {
                    const hasSubMenu = !!subMenu?.subMenus.length;
                    const DynamicTag = hasSubMenu ? 'button' : 'a';
                    return (
                      <li key={subMenu.id}>
                        <DynamicTag
                          className={clsx(
                            'nav__sub-menu-link nav__sub-menu-link--full-width',
                            {
                              'nav__sub-menu-link--open':
                                tertiaryOpen && tertiaryMenuId === subMenu.id,
                              'nav__sub-menu-link--no-sub': !hasSubMenu,
                            }
                          )}
                          {...{
                            href: !hasSubMenu ? subMenu.url : null,
                            onClick: hasSubMenu
                              ? () => handleTertiaryMenu(subMenu.id)
                              : () =>
                                  handleDataLayer(menu.title, 2, subMenu.title),
                            'aria-expanded': hasSubMenu
                              ? tertiaryOpen && tertiaryMenuId === subMenu.id
                              : null,
                            'aria-controls': hasSubMenu ? 'nav-tertiary' : null,
                          }}
                        >
                          <span>{subMenu.title}</span>
                        </DynamicTag>
                      </li>
                    );
                  })}
                </ul>
                {isMobile && (
                  <FeaturedPages
                    featuredPages={featuredPages}
                    variant="block"
                  />
                )}
              </div>
            </NavTransition>
          </>
        ) : null}
      </div>

      {isOpen && hasFeaturedPages && !isMobile && (
        <FeaturedPages featuredPages={featuredPages} variant="menu" />
      )}

      {tertiaryMenu}
    </section>
  );
};

const FeaturedPages = ({
  featuredPages,
  variant = 'menu',
}: {
  featuredPages: FeaturedPageEntity[];
  variant?: 'menu' | 'block';
}) => {
  const baseClasses = {
    wrapper: 'nav__featured',
    inner: 'nav__featured-inner',
    listItem: 'nav__featured-page-item',
  };

  const variantClasses = {
    menu: {
      wrapper: 'nav__featured-menu nav__featured-menu--open',
      inner: 'nav__featured-menu-inner',
      listItem: 'nav__featured-page',
    },
    block: {
      wrapper: 'nav__featured-page-block',
      inner: 'nav__featured-page-block-inner',
      listItem: 'nav__featured-page-block-list-item',
    },
  };

  const classes = variantClasses[variant];

  return (
    <div className={`${baseClasses.wrapper} ${classes.wrapper}`}>
      <div className={`${baseClasses.inner} ${classes.inner}`}>
        <ul className="nav__featured-page-block-list">
          {featuredPages?.map((page, index) => (
            <li
              key={`${page?.title}-${index}`}
              className={`${baseClasses.listItem} ${classes.listItem}`}
            >
              <a
                className="nav__featured-page-block-link"
                href={page?.url || '#'}
                onClick={() =>
                  page?.title && handleDataLayer(page.title, 2, page.title)
                }
              >
                {page?.image && (
                  <img
                    src={page?.image}
                    alt={page?.title}
                    className="nav__featured-page-block-image"
                  />
                )}
                {page?.title && (
                  <span className="nav__featured-page-block-title">
                    {page.title}
                  </span>
                )}
                <div className="nav__featured-page-block-type-wrapper">
                  {page?.tag && (
                    <span className="nav__featured-page-block-type">
                      {page.tag}
                    </span>
                  )}
                </div>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const TertiaryMenu = ({
  isOpen,
  menu,
  rootMenuTitle,
  tertiaryContentTopRef,
  close,
}: {
  isOpen: boolean;
  menu?: NavigationSubMenu;
  rootMenuTitle?: string;
  tertiaryContentTopRef: React.MutableRefObject<HTMLDivElement>;
  close: () => void;
}) => {
  const altLayout = !menu?.panelLinkText && !menu?.panelCopy;
  const linksRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLAnchorElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [isScrolled] = useIsScrolled(innerRef);
  const { resourceStrings } = data;

  const onTransitionComplete = () => {
    const firstFocusableEl = linksRef.current.querySelector<
      HTMLAnchorElement | HTMLButtonElement
    >('a, button');

    firstFocusableEl?.focus();
  };

  return (
    <div
      id="nav-tertiary"
      className={clsx('nav__tertiary-menu', {
        'nav__tertiary-menu--open': isOpen,
        scrolled: isScrolled,
      })}
    >
      {menu ? (
        <div className="nav__tertiary-menu-inner" ref={innerRef}>
          <div className="nav__tertiary-menu-content">
            <NavTransition
              contentRef={tertiaryContentTopRef}
              contentKey={menu.id}
            >
              <>
                <button
                  type="button"
                  className="nav__tertiary-menu-back"
                  onClick={close}
                >
                  <i className="icon-arrow-left" />
                  <span>{resourceStrings.back}</span>
                </button>
                <div
                  className={clsx('nav__tertiary-menu-top', {
                    'nav__tertiary-menu-top--no-min': altLayout,
                  })}
                  ref={tertiaryContentTopRef}
                >
                  <h4 className="nav__tertiary-menu-title">
                    {menu.panelHeading}
                  </h4>
                  <p className="nav__tertiary-menu-copy">{menu.panelCopy}</p>
                </div>
              </>
            </NavTransition>
            <NavTransition
              contentRef={linksRef}
              contentKey={menu.id}
              delay={0.2}
              onComplete={onTransitionComplete}
            >
              <div
                className={clsx('nav__tertiary-menu-links', {
                  'nav__tertiary-menu-links--small-gap': altLayout,
                })}
                ref={linksRef}
              >
                {menu.url && menu.panelLinkText ? (
                  <a
                    className="nav__tertiary-menu-page-link"
                    href={menu.url}
                    onClick={() =>
                      handleDataLayer(rootMenuTitle, 3, menu.panelLinkText)
                    }
                  >
                    {menu.panelLinkText}
                  </a>
                ) : null}
                {menu.subMenus.length ? (
                  <ul className="nav__tertiary-menu-links-list">
                    {menu.subMenus.map(item => (
                      <li key={item.id}>
                        <a
                          className="nav__tertiary-menu-link"
                          href={item.url}
                          onClick={() =>
                            handleDataLayer(rootMenuTitle, 3, item.title)
                          }
                        >
                          <span>{item.title}</span>
                          <i className="icon-arrow-right" />
                        </a>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </NavTransition>
          </div>
          {menu.preview ? (
            <NavTransition
              contentRef={previewRef}
              contentKey={menu.id}
              delay={0.4}
            >
              <a
                href={menu.preview.url}
                className="nav__preview-block"
                ref={previewRef}
                onClick={() =>
                  handleDataLayer(rootMenuTitle, 3, menu.preview.title)
                }
              >
                <div className="nav__preview-block-content">
                  {menu.preview.tag ? (
                    <div className="nav__preview-block-tag">
                      {menu.preview.tag}
                    </div>
                  ) : null}
                  <div className="nav__preview-block-title">
                    {menu.preview.title}
                  </div>
                  <div className="nav__preview-block-copy">
                    {menu.preview.text}
                  </div>
                </div>
                {menu.preview.image ? (
                  <img
                    className="nav__preview-block-image"
                    src={menu.preview.image}
                    alt=""
                    width={180}
                    height={196}
                  />
                ) : null}
              </a>
            </NavTransition>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const LanguageSelector = ({
  onOpen,
  isOpen,
  setIsOpen,
}: {
  onOpen?: () => void;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const { languages } = data;
  const [selected, setSelected] = useState(
    languages.find(item => item.isCurrentLang).text
  );
  const isDesktop = useIsBreakpoint();
  const listRef = useRef<HTMLUListElement>(null);

  const toggleMenu = () => setIsOpen(!isOpen);

  const handleClick = (url: string, name: string) => {
    window.location.href = url;
    setSelected(name);
  };

  useEffect(() => {
    if (!isOpen) return;
    if (onOpen) onOpen();
    if (!isDesktop) listRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [isOpen, onOpen, isDesktop]);

  return languages.length > 1 ? (
    <div
      className={clsx('language-selector', {
        'language-selector--open': isOpen,
      })}
    >
      <button
        type="button"
        title="Language"
        className="language-selector__button"
        onClick={toggleMenu}
      >
        <span className="language-selector__button-inner">
          <i className="icon-language" />
          <span>{selected}</span>
        </span>
        <i className="language-selector__button-chevron icon-chevron-down" />
      </button>
      <div className="language-selector__menu">
        <ul className="language-selector__menu-list" ref={listRef}>
          {languages.map(item => (
            <li key={item.text}>
              <button
                type="button"
                className="language-selector__menu-item"
                onClick={() => handleClick(item.url, item.text)}
                tabIndex={isOpen ? 0 : -1}
              >
                <span>{item.text}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  ) : null;
};

const NavTransition = ({
  contentRef,
  contentKey,
  onComplete,
  children,
  delay = 0,
}: PropsWithChildren<{
  contentRef: React.MutableRefObject<HTMLElement>;
  contentKey: string;
  onComplete?: () => void;
  delay?: number;
}>) => {
  const [animating, setAnimating] = useState(false);

  const timeout = 300,
    duration = timeout / 1000; // Convert to seconds

  useEffect(() => {
    if (animating) {
      contentRef.current.classList.add(ANIMATION_CLASSES.animating);
      contentRef.current.classList.remove(ANIMATION_CLASSES.animated);
    } else {
      contentRef.current.classList.remove(ANIMATION_CLASSES.animating);
      contentRef.current.classList.add(ANIMATION_CLASSES.animated);
    }
  }, [animating, contentRef]);
  return (
    <SwitchTransition mode="out-in">
      <Transition
        timeout={timeout}
        appear={true}
        in={true}
        key={contentKey}
        nodeRef={contentRef}
        onExit={() => {
          setAnimating(true);
          gsap.to(contentRef.current, {
            opacity: 0,
            y: -40,
            delay,
            duration,
            onComplete: () => setAnimating(false),
          });
        }}
        onEnter={() => {
          setAnimating(true);
          gsap.from(contentRef.current, {
            opacity: 0,
            y: 40,
            delay,
            duration,
            onComplete: () => {
              setAnimating(false);
              onComplete?.();
            },
          });
        }}
      >
        {children}
      </Transition>
    </SwitchTransition>
  );
};

rootRender('react-nav', <Nav />);
